/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../api/axios";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const money = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

async function downloadBlob(filename, blob) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function PayrollHistory() {
  const navigate = useNavigate();
  const qs = useQuery();

  const runId = qs.get("run_id");
  const tab = qs.get("tab") || (runId ? "summary" : "runs");

  // shared filters
  const [pfno, setPfno] = useState(qs.get("pfno") || "");
  const [page, setPage] = useState(Number(qs.get("page") || 1));
  const [perPage, setPerPage] = useState(Number(qs.get("per_page") || 50));

  // list runs
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // drilldown
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [summary, setSummary] = useState(null);

  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);

  const setUrl = (patch) => {
    const next = new URLSearchParams(qs.toString());
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    navigate({ search: next.toString() });
  };

  // keep local state in sync when url changes
  useEffect(() => {
    setPfno(qs.get("pfno") || "");
    setPage(Number(qs.get("page") || 1));
    setPerPage(Number(qs.get("per_page") || 50));
  }, [qs.toString()]);

  const loadRuns = async () => {
    setRunsLoading(true);
    try {
      const res = await axios.get("/payroll-run", { params: { per_page: 50 } });
      setRuns(res.data?.data || []);
    } finally {
      setRunsLoading(false);
    }
  };

  const loadDrilldown = async () => {
    if (!runId) return;

    setLoading(true);
    try {
      if (tab === "summary") {
        const res = await axios.get(`/payroll-run/${runId}/summary`);
        setSummary(res.data);
        return;
      }

      if (tab === "items") {
        const res = await axios.get(`/payroll-run/${runId}/items`, {
          params: { pfno: pfno || undefined, page, per_page: perPage },
        });
        setItems(res.data?.data || []);
        setMeta({
          current_page: res.data?.current_page || 1,
          last_page: res.data?.last_page || 1,
          total: res.data?.total || 0,
        });
        return;
      }

      if (tab === "taxes") {
        const res = await axios.get(`/payroll-run/${runId}/taxes`, {
          params: { pfno: pfno || undefined, page, per_page: perPage },
        });
        setTaxes(res.data?.data || []);
        setMeta({
          current_page: res.data?.current_page || 1,
          last_page: res.data?.last_page || 1,
          total: res.data?.total || 0,
        });
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!runId) loadRuns();
  }, [runId]);

  useEffect(() => {
    if (runId) loadDrilldown();
  }, [runId, tab, pfno, page, perPage]);

  // ✅ FIX: use Cell: (cell) => ... so no "row" prop-types warning
  const runsColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Month", accessor: "month" },
      { Header: "Status", accessor: "status" },
      { Header: "Generation", accessor: "generation_status" },
      { Header: "Finalized At", accessor: "finalized_at" },
      {
        Header: "Open",
        accessor: "open",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};
          return (
            <MDButton
              size="small"
              color="info"
              onClick={() => setUrl({ run_id: r.id, tab: "summary", page: 1 })}
              disabled={!r?.id}
            >
              View
            </MDButton>
          );
        },
      },
    ],
    [navigate, qs.toString()]
  );

  const itemsColumns = useMemo(
    () => [
      { Header: "PFNO", accessor: "pfno" },
      { Header: "Gross", accessor: "gross", Cell: (cell) => money(cell?.value) },
      {
        Header: "Deductions",
        accessor: "total_deductions",
        Cell: (cell) => money(cell?.value),
      },
      { Header: "Net", accessor: "net", Cell: (cell) => money(cell?.value) },
      {
        Header: "Payslip",
        accessor: "payslip",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};
          const pf = r?.pfno;
          return (
            <MDButton
              size="small"
              color="success"
              disabled={!runId || !pf}
              onClick={() =>
                navigate(
                  `/payroll/payslip?run_id=${encodeURIComponent(runId)}&pfno=${encodeURIComponent(
                    pf
                  )}`
                )
              }
            >
              View
            </MDButton>
          );
        },
      },
    ],
    [navigate, runId]
  );

  const taxesColumns = useMemo(
    () => [
      { Header: "PFNO", accessor: "pfno" },

      // ✅ NEW audit columns
      { Header: "Policy ID", accessor: "policy_id" },
      { Header: "Rent Mode", accessor: "rent_relief_mode" },
      {
        Header: "Approved Claims",
        accessor: "approved_claims_total",
        Cell: (cell) => money(cell?.value),
      },

      // existing columns
      { Header: "Annual Gross", accessor: "annual_gross", Cell: (cell) => money(cell?.value) },
      { Header: "Chargeable", accessor: "chargeable_income", Cell: (cell) => money(cell?.value) },
      { Header: "Annual PIT", accessor: "annual_pit", Cell: (cell) => money(cell?.value) },
      { Header: "Monthly PAYE", accessor: "monthly_paye", Cell: (cell) => money(cell?.value) },
    ],
    []
  );

  const header = () => {
    if (!runId) {
      return (
        <MDBox>
          <MDTypography variant="h5">Payroll History</MDTypography>
          <MDTypography variant="button" color="text">
            Click a run to drill down into Items / Taxes / Summary.
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox>
        <MDButton color="dark" size="small" onClick={() => setUrl({ run_id: null, tab: null })}>
          ← Back to Runs
        </MDButton>

        <MDBox mt={2}>
          <MDTypography variant="h5">Payroll Run #{runId}</MDTypography>
          <MDTypography variant="button" color="text">
            Drilldown: Summary / Items / Taxes
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  const tabButtons = () => {
    if (!runId) return null;

    return (
      <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
        <MDButton
          color={tab === "summary" ? "info" : "dark"}
          onClick={() => setUrl({ tab: "summary" })}
        >
          Summary
        </MDButton>
        <MDButton
          color={tab === "items" ? "info" : "dark"}
          onClick={() => setUrl({ tab: "items", page: 1 })}
        >
          Items
        </MDButton>
        <MDButton
          color={tab === "taxes" ? "info" : "dark"}
          onClick={() => setUrl({ tab: "taxes", page: 1 })}
        >
          Taxes
        </MDButton>
      </MDBox>
    );
  };

  const filtersBar = () => {
    if (!runId || tab === "summary") return null;

    return (
      <MDBox mt={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <MDBox width={220}>
          <MDInput label="PFNO (optional)" value={pfno} onChange={(e) => setPfno(e.target.value)} />
        </MDBox>

        <MDBox width={140}>
          <MDInput
            label="Per Page"
            type="number"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value || 50))}
          />
        </MDBox>

        <MDButton
          color="info"
          disabled={loading}
          onClick={() => setUrl({ pfno, page: 1, per_page: perPage })}
        >
          Apply
        </MDButton>

        <MDButton
          color="secondary"
          disabled={loading}
          onClick={async () => {
            try {
              const endpoint =
                tab === "items"
                  ? `/payroll-run/${runId}/export/items.csv`
                  : `/payroll-run/${runId}/export/taxes.csv`;

              const res = await axios.get(endpoint, {
                params: { pfno: pfno || undefined },
                responseType: "blob",
              });

              const fname =
                tab === "items"
                  ? `payroll_items_run_${runId}.csv`
                  : `payroll_taxes_run_${runId}.csv`;
              await downloadBlob(fname, res.data);
            } catch (e) {
              alert("Export failed.");
            }
          }}
        >
          Export CSV
        </MDButton>
      </MDBox>
    );
  };

  const pager = () => {
    if (!runId || tab === "summary") return null;

    return (
      <MDBox mt={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <MDTypography variant="button" color="text">
          Page {meta.current_page} of {meta.last_page} • Total {meta.total}
        </MDTypography>

        <MDButton
          size="small"
          color="dark"
          disabled={loading || meta.current_page <= 1}
          onClick={() => setUrl({ page: meta.current_page - 1 })}
        >
          Prev
        </MDButton>

        <MDButton
          size="small"
          color="dark"
          disabled={loading || meta.current_page >= meta.last_page}
          onClick={() => setUrl({ page: meta.current_page + 1 })}
        >
          Next
        </MDButton>
      </MDBox>
    );
  };

  const renderSummary = () => {
    if (!summary) return null;

    const r = summary.run || {};
    const it = summary.items || {};
    const tx = summary.taxes || {};

    const exportReport = async (kind) => {
      if (!runId) return;

      try {
        let endpoint = "";
        let fname = "";

        if (kind === "netpay") {
          endpoint = `/payroll-run/${runId}/export/netpay.csv`;
          fname = `payroll_netpay_run_${runId}.csv`;
        } else if (kind === "paye") {
          endpoint = `/payroll-run/${runId}/export/paye.csv`;
          fname = `payroll_paye_run_${runId}.csv`;
        } else if (kind === "pension") {
          endpoint = `/payroll-run/${runId}/export/pension.csv`;
          fname = `payroll_pension_run_${runId}.csv`;
        }

        const res = await axios.get(endpoint, { responseType: "blob" });
        await downloadBlob(fname, res.data);
      } catch (e) {
        alert("Export failed.");
      }
    };

    return (
      <MDBox mt={3}>
        {/* ✅ NEW: Summary report downloads */}
        <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
          <MDButton
            color="success"
            size="small"
            disabled={!runId || loading}
            onClick={() => exportReport("netpay")}
          >
            Net Pay CSV
          </MDButton>

          <MDButton
            color="info"
            size="small"
            disabled={!runId || loading}
            onClick={() => exportReport("paye")}
          >
            PAYE CSV
          </MDButton>

          <MDButton
            color="warning"
            size="small"
            disabled={!runId || loading}
            onClick={() => exportReport("pension")}
          >
            Pension CSV
          </MDButton>
        </MDBox>

        <MDBox display="flex" gap={3} flexWrap="wrap">
          <MDBox p={2} sx={{ border: "1px solid #eee", borderRadius: 2, minWidth: 260 }}>
            <MDTypography variant="h6">Run</MDTypography>
            <MDTypography variant="button">Month: {r.month}</MDTypography>
            <br />
            <MDTypography variant="button">Status: {r.status}</MDTypography>
            <br />
            <MDTypography variant="button">
              Generation: {r.generation_status} ({r.generation_processed}/{r.generation_total})
            </MDTypography>
          </MDBox>

          <MDBox p={2} sx={{ border: "1px solid #eee", borderRadius: 2, minWidth: 260 }}>
            <MDTypography variant="h6">Payroll Totals</MDTypography>
            <MDTypography variant="button">Staff Count: {it.staff_count}</MDTypography>
            <br />
            <MDTypography variant="button">Gross: {money(it.total_gross)}</MDTypography>
            <br />
            <MDTypography variant="button">Deductions: {money(it.total_deductions)}</MDTypography>
            <br />
            <MDTypography variant="button">Net: {money(it.total_net)}</MDTypography>
          </MDBox>

          <MDBox p={2} sx={{ border: "1px solid #eee", borderRadius: 2, minWidth: 260 }}>
            <MDTypography variant="h6">Tax Totals</MDTypography>
            <MDTypography variant="button">Rows: {tx.tax_rows}</MDTypography>
            <br />
            <MDTypography variant="button">
              Total Monthly PAYE: {money(tx.total_monthly_paye)}
            </MDTypography>
            <br />
            <MDTypography variant="button">
              Total Annual PIT: {money(tx.total_annual_pit)}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mt={3}>
          <MDTypography variant="h6">Top 10 PAYE (Audit)</MDTypography>
          <DataTable
            table={{
              columns: [
                { Header: "PFNO", accessor: "pfno" },
                {
                  Header: "Monthly PAYE",
                  accessor: "monthly_paye",
                  Cell: (cell) => money(cell?.value),
                },
                {
                  Header: "Annual PIT",
                  accessor: "annual_pit",
                  Cell: (cell) => money(cell?.value),
                },
              ],
              rows: summary.top_paye || [],
            }}
            entriesPerPage={false}
            showTotalEntries={false}
            canSearch
            pagination
            noEndBorder
          />
        </MDBox>
      </MDBox>
    );
  };
  return (
    <MDBox p={3}>
      {header()}
      {tabButtons()}

      {!runId && (
        <MDBox mt={3}>
          <MDButton color="info" onClick={loadRuns} disabled={runsLoading}>
            Refresh
          </MDButton>

          <MDBox mt={2}>
            <DataTable
              table={{ columns: runsColumns, rows: runs }}
              entriesPerPage={false}
              showTotalEntries={false}
              canSearch
              pagination
              noEndBorder
            />
          </MDBox>
        </MDBox>
      )}

      {runId && (
        <MDBox>
          {filtersBar()}
          {pager()}

          {tab === "summary" && renderSummary()}

          {tab === "items" && (
            <MDBox mt={3}>
              <DataTable
                table={{ columns: itemsColumns, rows: items }}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                pagination={false}
                noEndBorder
              />
            </MDBox>
          )}

          {tab === "taxes" && (
            <MDBox mt={3}>
              <DataTable
                table={{ columns: taxesColumns, rows: taxes }}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                pagination={false}
                noEndBorder
              />
            </MDBox>
          )}
        </MDBox>
      )}
    </MDBox>
  );
}
